/**
 * Phase N — /impact
 *
 * Public impact page. Renders LIVE verification counts from
 * /api/catalog/verification-summary. Copy avoids fabricated numeric claims;
 * see backend/tests/test_positioning.py for the enforced substring blocklist.
 * Only defensible categories and qualitative language, plus the live graph
 * counts pulled from the endpoint on mount.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import LegalReviewBanner from "@/components/LegalReviewBanner";
import { api } from "@/lib/api";

// Six illustrative pathways. NOT real case studies. Framing is load-bearing:
// every card renders an "Illustrative" chip AND a caveat line, and the section
// intro paragraph on /impact reinforces that these are composites.
const IMPACT_VIGNETTES = [
  {
    slug: "cc-to-uf",
    title: "Community College → University of Florida",
    body: "A Valencia student who sequences the Common Prerequisites Manual correctly from their first semester, hits UF's competitive GPA floor for their intended major, and lands on a defensible essay narrative before transfer application windows open.",
  },
  {
    slug: "cc-to-highly-selective",
    title: "Community College → Highly Selective University",
    body: "A community-college student targeting a highly selective destination outside Florida. Beyond Valencia builds the Florida-side plan; when the target list warrants it, our partnership with Ivy League Consulting brings elite-transfer strategy on top.",
  },
  {
    slug: "rejection-to-transfer",
    title: "Freshman Rejection → Transfer Acceptance",
    body: "A student who was rejected from their target school out of high school, enrolls at Valencia (or another Florida community college), rebuilds their transcript against the destination's Common Prerequisites, and re-applies as a transfer with a stronger, cheaper record.",
  },
  {
    slug: "first-gen-to-flagship",
    title: "First-Generation → Florida Flagship",
    body: "A first-generation college student navigating financial aid, DirectConnect eligibility, and articulation with less family guidance than their peers. Our verified graph and adviser touchpoints reduce the number of avoidable mistakes between orientation and transfer.",
  },
  {
    slug: "adult-learner",
    title: "Adult Learner → Career Reinvention",
    body: "A returning adult learner using the Florida community-college system as a controlled, affordable on-ramp into a new field. Sequencing, credit-for-prior-learning strategy, and destination-major research shape the plan.",
  },
  {
    slug: "cc-to-lower-debt",
    title: "Community College → Lower Total Debt",
    body: "A student for whom two years of Florida community college — followed by a targeted transfer into a four-year destination — is chosen specifically to lower total undergraduate debt without sacrificing destination quality or timeline.",
  },
];

function StatCard({ label, value, testId }) {
  return (
    <div className="border border-stone/40 bg-bone-dim p-6 text-center" data-testid={testId}>
      <div className="font-serif text-4xl sm:text-5xl text-ivy tabular-nums" data-testid={`${testId}-value`}>
        {value === null || value === undefined ? "—" : value}
      </div>
      <div className="mt-2 text-[10px] uppercase tracking-[0.22em] text-brass font-semibold">{label}</div>
    </div>
  );
}

function Section({ eyebrow, title, id, testId, children }) {
  return (
    <section id={id} data-testid={testId} className="border-b border-stone/20 py-14 sm:py-16">
      <div className="text-[10px] uppercase tracking-[0.22em] text-brass font-semibold">{eyebrow}</div>
      <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-ivy tracking-tight">{title}</h2>
      <div className="mt-6 max-w-3xl text-ink-soft leading-relaxed space-y-4">{children}</div>
    </section>
  );
}

export default function Impact() {
  const [summary, setSummary] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/catalog/verification-summary")
      .then((r) => { if (!cancelled) setSummary(r.data); })
      .catch((e) => { if (!cancelled) setErr(String(e)); });
    return () => { cancelled = true; };
  }, []);

  return (
    <Layout>
      <SEO
        title="Impact | Beyond Valencia"
        description="What Beyond Valencia is building — a verified transfer requirement graph, diagnostic tools, and adviser network — and how we hold ourselves accountable to the students we serve."
        canonical="/impact"
        ogType="article"
      />
      <LegalReviewBanner />

      {/* Hero */}
      <section className="bg-ivy text-bone" data-testid="impact-hero">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 pt-20 pb-16">
          <div className="text-[10px] uppercase tracking-[0.22em] text-brass-soft font-semibold">Impact</div>
          <h1
            className="mt-3 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
            data-testid="impact-h1"
          >
            The best measure of our work is the graph we&apos;ve verified.
          </h1>
          <p className="mt-6 max-w-2xl text-bone-dim text-lg leading-relaxed">
            Beyond Valencia is early in its journey. We are choosing to publish the numbers we can defend rather
            than the numbers that would sound bigger.
          </p>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        {/* Live counts */}
        <section className="py-12 border-b border-stone/20" data-testid="impact-live-counts">
          <div className="text-[10px] uppercase tracking-[0.22em] text-brass font-semibold">
            Live verification counts
          </div>
          <h2 className="mt-3 font-serif text-2xl sm:text-3xl text-ivy tracking-tight">
            The graph, as of right now.
          </h2>
          <p className="mt-4 text-sm text-stone">
            Data source:{" "}
            <code className="text-xs bg-bone-dim border border-stone/30 px-1.5 py-0.5">
              GET /api/catalog/verification-summary
            </code>
            {summary && summary.generated_at && (
              <>
                {" · Generated "}
                <span data-testid="impact-generated-at">
                  {new Date(summary.generated_at).toLocaleString()}
                </span>
              </>
            )}
          </p>
          {err && (
            <p className="mt-3 text-sm text-crimson" data-testid="impact-error">
              Unable to load current counts. The values shown below may be cached.
            </p>
          )}
          <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <StatCard label="Institutions verified" value={summary?.institutions_verified} testId="impact-institutions-verified" />
            <StatCard label="Majors verified" value={summary?.majors_verified} testId="impact-majors-verified" />
            <StatCard label="Roadmaps verified" value={summary?.roadmaps_verified} testId="impact-roadmaps-verified" />
            <StatCard label="Programs verified" value={summary?.programs_verified} testId="impact-programs-verified" />
            <StatCard label="Verification records" value={summary?.verification_records_verified} testId="impact-verification-records-verified" />
            <StatCard label="Prereqs traceable" value={summary?.program_requirements_traceable} testId="impact-program-requirements-traceable" />
          </div>
          <p className="mt-4 text-xs text-stone leading-relaxed">
            These counts represent verified records only. Placeholder, draft, and awaiting-review entries do not
            appear on the public site and are excluded from the totals above.
          </p>
        </section>

        <Section eyebrow="What we build" title="Products, not press releases." id="products" testId="impact-products">
          <p>
            The impact of Beyond Valencia is measured by what a student can actually do with the site. A diagnostic
            that reads a transcript in under a minute. Pair pages that show every prerequisite for a specific
            Valencia → UF program, sourced live. An adviser network that treats those documents as the ground truth.
          </p>
          <p>
            Every product ships with a verification bar we hold ourselves to. Every claim maps to a source URL.
          </p>
        </Section>

        {/* Illustrative pathways — NOT real case studies. Framing is load-bearing. */}
        <section id="illustrative" data-testid="impact-illustrative" className="border-b border-stone/20 py-14 sm:py-16">
          <div className="text-[10px] uppercase tracking-[0.22em] text-brass font-semibold">Illustrative pathways</div>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-ivy tracking-tight">
            Six trajectories our methodology is built to support.
          </h2>
          <p className="mt-6 max-w-3xl text-ink-soft leading-relaxed" data-testid="impact-illustrative-disclaimer">
            The following are illustrative pathways — composite scenarios drawn from real transfer situations our
            methodology supports. Real student case studies will appear here as they are approved for publication.
          </p>
          <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {IMPACT_VIGNETTES.map((v) => (
              <article
                key={v.slug}
                data-testid={`impact-vignette-${v.slug}`}
                className="border border-stone/30 bg-bone p-6 flex flex-col"
              >
                <span
                  className="self-start border border-brass text-ivy uppercase tracking-[0.18em] text-[10px] font-semibold py-1 px-2"
                  data-testid={`impact-vignette-${v.slug}-chip`}
                >
                  Illustrative
                </span>
                <h3 className="mt-4 font-serif text-xl text-ivy tracking-tight leading-snug">{v.title}</h3>
                <p className="mt-3 text-sm text-ink-soft leading-relaxed">{v.body}</p>
                <p
                  className="mt-4 pt-4 border-t border-stone/20 text-[11px] leading-relaxed text-stone italic"
                  data-testid={`impact-vignette-${v.slug}-caveat`}
                >
                  Illustrative pathway — composite of real transfer scenarios our methodology supports.
                </p>
              </article>
            ))}
          </div>
        </section>

        <Section eyebrow="Who we serve" title="A growing community of transfer students." id="community" testId="impact-community">
          <p>
            Beyond Valencia is Florida-anchored — Valencia, Seminole, Santa Fe, Daytona, Broward, Miami Dade, and
            every other community college in the state — with an emerging national footprint through our partnership
            network. Every free tool is open. The paid tiers are for students who want a human strategist reading
            the same graph.
          </p>
          <p>
            We support a growing community of ambitious students on non-traditional paths — community college,
            gap-year, and students recovering from a rejected freshman cycle. Our audience is defined by the choices
            we serve, not by advertising spend.
          </p>
        </Section>

        <Section eyebrow="How we work with schools" title="Institutional partnerships." id="institutional" testId="impact-institutional">
          <p>
            Beyond Valencia works with school counselors, alumni networks, and college-access nonprofits to make
            transfer intelligence available to the students who most need it — often at no cost when the delivery
            fits our free tools. Formal institutional partnerships are announced individually as they are executed.
          </p>
          <p>
            The largest ongoing partnership is with{" "}
            <Link to="/partners/ivy-league-consulting" className="text-ivy hover:text-brass underline" data-testid="impact-partnership-link">
              Ivy League Consulting
            </Link>
            , covering elite and Ivy transfer destinations.
          </p>
        </Section>

        <Section eyebrow="What comes next" title="An ambitious roadmap, on the record." id="roadmap" testId="impact-roadmap">
          <ul className="list-disc pl-5 space-y-2">
            <li>Expand the verified requirement graph to every Florida SUS destination.</li>
            <li>Publish original transfer-outcomes research on the pathways students actually take.</li>
            <li>Grow the adviser network with strategists who fit our editorial and verification standards.</li>
            <li>Ship the first comprehensive open catalog of Florida transfer articulation.</li>
          </ul>
          <p>
            Investors, school partners, and researchers interested in this trajectory can reach us at{" "}
            {/* TODO: confirm mailbox routing before publishing */}
            <a href="mailto:hello@beyondvalencia.com" className="text-ivy hover:text-brass underline" data-testid="impact-contact-email">
              hello@beyondvalencia.com
            </a>
            .
          </p>
        </Section>
      </div>
    </Layout>
  );
}
